//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { deserializeEarliestAudienceInsightDate } from '@apis/gateway-instagram/instagramAudienceMetricsApiUtils';
import {
  IInstagramAudienceMetricsDateTimeResponse,
  IInstagramDailyAudienceMetricsRequest,
  IInstagramDailyAudienceMetricsResponse,
  IInstagramLifetimeAudienceMetricsResponse,
  IInstagramOnlineFollowersHeatmap,
  IInstagramWeekAudienceMetricsResponse,
} from '@apis/gateway-instagram/instagramAudienceMetricsModel';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

function instagramGatewayFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch('instagram_dashboard_gateway', endpoint, init);
}

function audienceMetricsFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return instagramGatewayFetch(`/api/v1/audience-metrics/${endpoint}`, init);
}

/**
 * Returns the audience metrics for a client within a given date range
 *
 * /api/v1/audience-metrics/daily
 */
export async function getDailyAudienceMetrics(
  requestParams: IInstagramDailyAudienceMetricsRequest,
  init?: RequestInit,
): Promise<IInstagramDailyAudienceMetricsResponse> {
  const params = makeParams(requestParams);
  const res = await audienceMetricsFetch(`daily?${params}`, init);
  return res.json<IInstagramDailyAudienceMetricsResponse>();
}

/**
 * Returns the audience metrics for a client within a given date range as a CSV
 *
 * /api/v1/audience-metrics/daily.csv
 */
export async function getDailyAudienceMetricsCsv(
  requestParams: IInstagramDailyAudienceMetricsRequest,
): Promise<string> {
  const params = makeParams(requestParams);
  const res = await audienceMetricsFetch(`daily.csv?${params}`);
  return res.text();
}

/**
 * Returns the most up to date week metrics we have for the current client
 *
 * /api/v1/audience-metrics/weekly/latest
 */
export async function getLatestWeeklyMetrics(): Promise<IInstagramWeekAudienceMetricsResponse> {
  const res = await audienceMetricsFetch('weekly/latest');
  return res.json();
}

/**
 * Returns the most up to date lifetime metrics we have for the current client
 *
 * /api/v1/audience-metrics/lifetime/latest
 */
export async function getLatestLifetimeMetrics(): Promise<IInstagramLifetimeAudienceMetricsResponse> {
  const res = await audienceMetricsFetch('lifetime/latest');
  return res.json();
}

/**
 * Returns the earliest insight's starting DateTime for the current client
 *
 * /api/v1/audience-metrics/earliest-datetime
 */
export async function getEarliestAudienceInsightDate(): Promise<IInstagramAudienceMetricsDateTimeResponse> {
  const res = await audienceMetricsFetch('earliest-datetime');
  return res.json<IInstagramAudienceMetricsDateTimeResponse>(
    deserializeEarliestAudienceInsightDate,
  );
}

/**
 * Fetch a heatmap of averaged weekly online follower data for the primary instagram account
 *
 * /api/v1/online-followers/averaged-week
 */
export async function getOnlineFollowersHeatmap(): Promise<IInstagramOnlineFollowersHeatmap> {
  const res = await instagramGatewayFetch(
    '/api/v1/online-followers/averaged-week',
  );
  return res.json();
}
